<template>
  <div>
    <!-- 히어로 -->
    <section class="hero">
      <div class="hero-photo" :style="{ backgroundImage: 'url(' + heroPhoto + ')' }"></div>
      <div class="hero-shade"></div>
      <div class="glow"></div>
      <div class="container hero-inner">
        <div class="hero-main">
          <span class="eyebrow">PROCESS AUTOMATION · TEST &amp; MEASUREMENT · SYSTEM INTEGRATION</span>
          <h1 class="hero-title">방산·연구기관 현장에 맞춘 <br>공정 자동화·시험 계측 시스템</h1>
          <p class="hero-sub">PLC·LabVIEW 제어 설계, DAQ 신호 분석, H/W·S/W 통합 개발. 요구 사양 검토부터 설계, 제작, 시험, 납품 후 지원까지 한 팀이 책임집니다.</p>
          <div class="hero-actions">
            <a class="btn btn-primary" :href="company.brochure" download>회사소개서 다운로드</a>
            <a class="btn btn-ghost-dark" href="#contact" @click.prevent="scrollTo('contact')">문의하기 →</a>
          </div>
        </div>
        <aside class="spec" aria-label="수행 범위">
          <div class="spec-label">SCOPE OF WORK</div>
          <div class="spec-row"><div class="spec-k">제어</div><div class="spec-v">PLC · LabVIEW · C++ · 통합 시스템 제어</div></div>
          <div class="spec-row"><div class="spec-k">계측</div><div class="spec-v">DAQ 신호 분석 · CAM 장비 설계</div></div>
          <div class="spec-row"><div class="spec-k">통합·공사</div><div class="spec-v">H/W·S/W 통합 · 정보통신 공사 · 소방 공사</div></div>
          <div class="spec-row"><div class="spec-k">자격</div><div class="spec-v">정보통신기사 · 무선설비기사 · 소방 안전관리 · 전자기기기능사</div></div>
        </aside>
      </div>
      <div class="container hero-trust">
        <div class="trust"><div class="trust-k">설립</div><div class="trust-v">{{ company.foundedYear }} · 대전</div></div>
        <div class="trust"><div class="trust-k">거래·협력 기관</div><div class="trust-v">{{ partnerCount }}곳</div></div>
        <div class="trust"><div class="trust-k">인증</div><div class="trust-v">ISO 9001 · 14001 · 45001</div></div>
        <div class="trust trust-wide"><div class="trust-k">주요 협력사</div><div class="trust-v">국방과학연구소 · 육군탄약지원사령부 · (주)한화 · SK온 · ETRI</div></div>
      </div>
    </section>

    <!-- 함께한 기관 로고 (한 줄) -->
    <section class="container logo-strip" v-reveal>
      <div class="logo-strip-label">함께한 기관·기업</div>
      <PartnerLogos :mono="true" />
    </section>

    <!-- 회사소개 -->
    <section id="about" class="section container anchor" v-reveal>
      <div class="intro">
        <div class="intro-head">
          <span class="eyebrow">ABOUT US</span>
          <h2 class="section-title">현장에 맞는 시스템을<br>처음부터 끝까지 만듭니다</h2>
          <div class="intro-photo"><img :src="aboutPhoto" alt="제어 시스템 랙" loading="lazy" /></div>
        </div>
        <div class="intro-body">
          <p>{{ company.nameKo }}은 {{ company.founded }} 대전에서 설립된 공정 자동화·시험 계측·시스템 통합 전문기업입니다. PLC와 LabVIEW·C++ 기반의 제어 소프트웨어, CAM 장비 설계, DAQ(Data Acquisition) 신호 분석 기술을 바탕으로 생산 현장과 연구기관의 시험 환경에 맞는 시스템을 설계하고 구축합니다.</p>
          <p>설립 첫해에 국방과학연구소, 국립소방연구원, 한국전자통신연구원(ETRI) 등 국가 연구기관과 (주)한화, (주)SK, (주)KDI 등 대기업·방산 기업의 협력업체·거래처로 등록되었습니다. 2023년에는 한국기계연구원, 한국전기연구원, (주)한화에어로스페이스가 더해져 현재 {{ partnerCount }}곳의 기관·기업과 함께하고 있습니다.</p>
          <p>기술영업·개발사업·기술연구소·운영 조직과 대표이사 직속 품질경영팀을 두어, 요구 사양 검토부터 설계, 제작, 시험, 납품 후 지원까지 한 팀이 책임집니다. 2023년 기업부설연구소로 인정받았고 ISO 9001·14001·45001 인증을 유지하고 있으며, 정보통신기사·무선설비기사·소방 안전관리 등 국가자격 보유 인력이 정보통신·소방 공사를 직접 수행합니다.</p>
          <p>NK System은 Next Key System의 약자입니다. 고객의 다음 과제를 여는 열쇠가 되겠다는 약속을 이름에 담았습니다.</p>
        </div>
      </div>
    </section>

    <!-- 회사 개요 (벤토) -->
    <section class="section container" v-reveal>
      <div class="section-head">
        <h2 class="section-title">회사 개요</h2>
        <span class="section-sub">{{ company.foundedYear }}년 설립 · {{ company.city }}</span>
      </div>
      <div class="bento">
        <div class="b b-dark b-wide">
          <div class="label accent">업종</div>
          <div class="big">공정 자동화<br>시스템 통합<br>H/W &amp; S/W 개발</div>
        </div>
        <div class="b card">
          <div class="label">설립일</div>
          <div><div class="num">{{ company.foundedYear }}</div><div class="small">3월 14일</div></div>
        </div>
        <div class="b card">
          <div class="label">대표이사</div>
          <div class="name">{{ company.ceo }}</div>
        </div>
        <div class="b card">
          <div class="label">거래·협력 기관</div>
          <div><div class="num">{{ partnerCount }}<span class="unit">곳</span></div><div class="small">국방·연구기관·대기업</div></div>
        </div>
        <div class="b card">
          <div class="label">인증 · 연구소</div>
          <div><div class="num">ISO<span class="unit">3종</span></div><div class="small">9001 · 14001 · 45001 · 기업부설연구소</div></div>
        </div>
        <a class="b b-wide b-brochure" :href="company.brochure" download>
          <div>
            <div class="brochure-title">회사소개서 (PDF)</div>
            <div class="brochure-sub">사업 영역과 수행 실적을 한 번에 확인하세요</div>
          </div>
          <span class="btn btn-primary btn-sm">다운로드</span>
        </a>
      </div>
    </section>

    <!-- 슬로건 -->
    <section class="section container" v-reveal>
      <SloganText />
    </section>


    <!-- 사업분야 -->
    <section id="business" class="section container anchor" v-reveal>
      <div class="section-head">
        <h2 class="section-title">사업분야</h2>
        <span class="section-sub">설계부터 제어 소프트웨어, 시험 장비, 통합 공사까지</span>
      </div>
      <div class="grid-3">
        <article v-for="b in business" :key="b.key" :id="b.key" class="biz card">
          <div class="biz-photo"><img :src="b.photo" :alt="b.title" loading="lazy" /></div>
          <div class="biz-head"><BizIcon :name="b.icon" :size="28" class="biz-icon" /></div>
          <div class="biz-title">{{ b.title }}</div>
          <p class="biz-sum">{{ b.summary }}</p>
          <div class="chips"><span v-for="t in b.tags" :key="t" class="chip">{{ t }}</span></div>
        </article>
      </div>
      <div class="grid-4 caps">
        <div class="cap card">
          <BizIcon name="chip" :size="32" class="cap-icon" />
          <h3 class="cap-title">하드웨어</h3>
          <ul class="cap-list"><li v-for="c in capabilities.hardware" :key="c">{{ c }}</li></ul>
        </div>
        <div class="cap card">
          <BizIcon name="code" :size="32" class="cap-icon" />
          <h3 class="cap-title">소프트웨어</h3>
          <ul class="cap-list"><li v-for="c in capabilities.software" :key="c">{{ c }}</li></ul>
        </div>
        <div class="cap card">
          <BizIcon name="nodes" :size="32" class="cap-icon" />
          <h3 class="cap-title">설계 · 제작</h3>
          <ul class="cap-list"><li v-for="c in capabilities.design" :key="c">{{ c }}</li></ul>
        </div>
        <div class="cap card">
          <BizIcon name="badge" :size="32" class="cap-icon" />
          <h3 class="cap-title">보유 자격</h3>
          <ul class="cap-list"><li v-for="c in capabilities.licenses" :key="c">{{ c }}</li></ul>
        </div>
      </div>
    </section>

    <!-- 주요 제품·장비 -->
    <section id="products" class="section container anchor" v-reveal>
      <div class="section-head">
        <h2 class="section-title">주요 제품 · 장비</h2>
        <span class="section-sub">방산 시험장비부터 민수 계측기, 연구용 자동화 시스템까지</span>
      </div>
      <ProductsSection />
    </section>

    <!-- 보유 인증서 -->
    <section class="section container" v-reveal>
      <div class="section-head">
        <h2 class="section-title">보유 인증서</h2>
        <span class="section-sub">기업부설연구소 · 직접생산확인 · ISO 3종</span>
      </div>
      <CertsSection />
    </section>

    <!-- 연혁 -->
    <section id="partners" class="section container history-sec anchor" v-reveal>
      <div class="history-intro">
        <h2 class="section-title">연혁</h2>
        <p class="history-desc">설립 첫해부터 국방·연구기관과 대기업 협력사로 등록되었고, 2023년 법인 전환과 기업부설연구소 인정, ISO 3종 인증을 거쳐 2024년 직접생산확인 증명을 획득했습니다.</p>
      </div>
      <HistoryList />
    </section>

    <!-- 파트너 -->
    <section class="section container" v-reveal>
      <div class="section-head">
        <h2 class="section-title">함께한 기관·기업</h2>
        <span class="section-sub">협력업체 · 거래처 등록 기준</span>
      </div>
      <PartnerLogos :mono="false" />
    </section>

    <!-- 조직도 -->
    <section class="section container" v-reveal>
      <div class="section-head">
        <h2 class="section-title">조직도</h2>
        <span class="section-sub">영업 · 개발 · 연구 · 운영, 품질보증부 직속</span>
      </div>
      <div class="org-wrap card"><OrgChart /></div>
    </section>

    <!-- 오시는 길 -->
    <section id="contact" class="section container anchor" v-reveal>
      <ContactBlock />
    </section>
  </div>
</template>

<script>
import { company, business, capabilities, partnerCount, heroPhoto, aboutPhoto } from '@/data/company';
import OrgChart from '@/components/OrgChart.vue';
import ProductsSection from '@/components/ProductsSection.vue';
import CertsSection from '@/components/CertsSection.vue';
import { scrollToSection } from '@/scroll';
import BizIcon from '@/components/BizIcon.vue';
import SloganText from '@/components/SloganText.vue';
import PartnerLogos from '@/components/PartnerLogos.vue';
import HistoryList from '@/components/HistoryList.vue';
import ContactBlock from '@/components/ContactBlock.vue';

export default {
  name: 'HomePage',
  components: { BizIcon, SloganText, PartnerLogos, HistoryList, ContactBlock, OrgChart, ProductsSection, CertsSection },
  data() { return { company, business, capabilities, partnerCount, heroPhoto, aboutPhoto }; },
  mounted() {
    if (this.$route.hash) this.$nextTick(() => scrollToSection(this.$route.hash.slice(1), false));
  },
  watch: {
    '$route.hash'(h) { if (h) scrollToSection(h.slice(1)); },
  },
  methods: { scrollTo: scrollToSection },
};
</script>

<style scoped>
.hero { background: var(--navy); position: relative; overflow: hidden; }
.hero-photo { position: absolute; inset: 0; background-size: cover; background-position: center 60%; opacity: 0.55; }
.hero-shade { position: absolute; inset: 0; background: linear-gradient(90deg, rgba(11,18,32,0.96) 0%, rgba(11,18,32,0.82) 45%, rgba(11,18,32,0.45) 100%), linear-gradient(180deg, rgba(11,18,32,0.2) 0%, rgba(11,18,32,0.9) 100%); }
.glow { position: absolute; right: -120px; top: -160px; width: 620px; height: 620px; border-radius: 999px; background: radial-gradient(circle, rgba(242,140,26,0.22) 0%, rgba(242,140,26,0) 65%); }
.hero-inner { position: relative; padding: 104px 0 72px 0; display: grid; grid-template-columns: minmax(0, 1.7fr) minmax(300px, 1fr); gap: 40px; align-items: end; }
.hero-main { display: flex; flex-direction: column; gap: 26px; }
.hero-title { font-size: 56px; line-height: 1.16; font-weight: 700; color: #ffffff; letter-spacing: -0.02em; }
.hero-sub { font-size: 19px; line-height: 1.65; color: var(--on-dark); max-width: 680px; }
.hero-actions { display: flex; align-items: center; gap: 16px; margin-top: 8px; flex-wrap: wrap; }
.spec { justify-self: end; width: 100%; max-width: 380px; background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.14); border-radius: 14px; padding: 22px 24px; display: flex; flex-direction: column; gap: 12px; backdrop-filter: blur(6px); }
.spec-label { font-size: 11px; font-weight: 700; letter-spacing: 0.18em; color: var(--orange); margin-bottom: 4px; }
.spec-row { display: grid; grid-template-columns: 76px 1fr; gap: 12px; padding-top: 12px; border-top: 1px solid rgba(255,255,255,0.1); font-size: 14px; line-height: 1.5; }
.spec-k { color: var(--on-dark); font-weight: 600; }
.spec-v { color: var(--on-dark-strong); }
.hero-trust { position: relative; display: grid; grid-template-columns: 1fr 1fr 1fr 2fr; gap: 24px; padding: 24px 0 48px 0; border-top: 1px solid rgba(255,255,255,0.14); }
.trust-k { font-size: 12px; font-weight: 600; color: var(--on-dark); letter-spacing: 0.06em; }
.trust-v { font-size: 16px; font-weight: 700; color: #ffffff; margin-top: 4px; }

.bento { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 16px; }
.b { border-radius: var(--radius); padding: 28px; display: flex; flex-direction: column; justify-content: space-between; min-height: 200px; }
.b-wide { grid-column: span 2; }
.b-dark { background: var(--navy); color: #ffffff; padding: 32px; min-height: 240px; }
.label { font-size: 13px; font-weight: 600; color: var(--muted); }
.label.accent { color: var(--orange); letter-spacing: 0.1em; }
.big { font-size: 30px; font-weight: 700; line-height: 1.3; }
.num { font-size: 40px; font-weight: 700; letter-spacing: -0.02em; line-height: 1.1; }
.unit { font-size: 20px; color: var(--muted); font-weight: 500; margin-left: 2px; }
.small { font-size: 14px; color: var(--muted); margin-top: 6px; }
.name { font-size: 32px; font-weight: 700; }
.b-brochure { background: #fff3e4; border: 1px solid #f7d9b4; flex-direction: row; align-items: center; gap: 20px; min-height: 0; }
.b-brochure:hover { color: inherit; border-color: var(--orange); }
.brochure-title { font-size: 20px; font-weight: 700; }
.brochure-sub { font-size: 14px; color: #9a6a35; margin-top: 4px; }

.anchor { scroll-margin-top: 72px; }
.logo-strip { padding-top: 40px; display: flex; flex-direction: column; gap: 14px; }
.logo-strip-label { font-size: 12px; font-weight: 600; color: var(--muted); letter-spacing: 0.14em; text-align: center; }
.intro { display: grid; grid-template-columns: 1fr 1.4fr; gap: 48px; align-items: start; }
.intro-head { display: flex; flex-direction: column; gap: 14px; }
.intro-photo { margin-top: 12px; border-radius: 12px; overflow: hidden; aspect-ratio: 4 / 3; }
.intro-photo img { width: 100%; height: 100%; object-fit: cover; }
.intro-body { display: flex; flex-direction: column; gap: 18px; font-size: 17px; line-height: 1.85; color: var(--ink-2); }
.intro-body p:first-child { color: var(--ink); }
.org-wrap { padding: 48px 32px; overflow-x: auto; }
.caps { margin-top: 16px; }
.cap { padding: 26px; display: flex; flex-direction: column; gap: 14px; }
.cap-icon { color: var(--orange); }
.cap-title { font-size: 18px; font-weight: 700; }
.cap-list { margin: 0; padding-left: 18px; color: var(--ink-2); font-size: 14px; line-height: 1.9; }
.section-sub.strong { font-weight: 600; letter-spacing: 0.12em; }
.biz { padding: 0 0 28px 0; display: flex; flex-direction: column; gap: 14px; min-height: 300px; color: inherit; overflow: hidden; transition: border-color .15s ease, transform .15s ease; }
.biz-photo { height: 170px; overflow: hidden; }
.biz-photo img { width: 100%; height: 100%; object-fit: cover; transition: transform .4s ease; }
.biz:hover .biz-photo img { transform: scale(1.04); }
.biz-head { padding: 22px 28px 0 28px; }
.biz-title, .biz-sum, .chips { padding-left: 28px; padding-right: 28px; }
.biz:hover { border-color: var(--orange); transform: translateY(-2px); }
.biz-icon { color: var(--orange); }
.biz-title { font-size: 22px; font-weight: 700; }
.biz-sum { font-size: 15px; line-height: 1.7; color: var(--ink-2); }
.chips { display: flex; gap: 8px; flex-wrap: wrap; margin-top: auto; }

.history-sec { display: grid; grid-template-columns: 1fr 2fr; gap: 48px; align-items: start; }
.history-intro { display: flex; flex-direction: column; gap: 12px; }
.history-desc { font-size: 15px; line-height: 1.7; color: var(--ink-2); }

@media (max-width: 900px) {
  .hero-inner { padding: 56px 0 40px 0; grid-template-columns: minmax(0, 1fr); gap: 28px; }
  .hero-main { gap: 18px; }
  .hero-title { font-size: 34px; }
  .hero-title br { display: none; }
  .hero-main .eyebrow { font-size: 11px; letter-spacing: 0.1em; }
  .hero-sub { font-size: 16px; }
  .spec { max-width: none; justify-self: stretch; }
  .hero-trust { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; padding-bottom: 40px; }
  .trust-wide { grid-column: span 2; }
  .bento { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; }
  .b { padding: 20px; min-height: 0; gap: 12px; }
  .b-dark { padding: 22px; }
  .big { font-size: 22px; }
  .num { font-size: 30px; }
  .name { font-size: 24px; }
  .b-brochure { flex-direction: column; align-items: flex-start; }
  .history-sec { grid-template-columns: minmax(0, 1fr); gap: 20px; }
  .caps { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .intro { grid-template-columns: minmax(0, 1fr); gap: 20px; }
  .intro-body { font-size: 16px; }
  .org-wrap { padding: 28px 16px; }
  .biz { min-height: 0; }
  .biz-photo { height: 150px; }
}
</style>
